<script setup lang="ts">
import SidebarLayout from '@/layout/SidebarLayout.vue';

// Full static class strings — Tailwind's scanner needs the literal
// "bg-gray-50" etc. to appear in source; `bg-${shade}` would not generate.
const swatches = [
  { name: 'black', bgClass: 'bg-black' },
  { name: 'white', bgClass: 'bg-white' },
  { name: 'gray-50', bgClass: 'bg-gray-50' },
  { name: 'gray-100', bgClass: 'bg-gray-100' },
  { name: 'gray-200', bgClass: 'bg-gray-200' },
  { name: 'gray-300', bgClass: 'bg-gray-300' },
  { name: 'gray-400', bgClass: 'bg-gray-400' },
  { name: 'gray-500', bgClass: 'bg-gray-500' },
  { name: 'gray-600', bgClass: 'bg-gray-600' },
  { name: 'gray-700', bgClass: 'bg-gray-700' },
  { name: 'gray-800', bgClass: 'bg-gray-800' },
  { name: 'gray-900', bgClass: 'bg-gray-900' },
];
</script>

<template>
  <SidebarLayout>
    <div class="mx-auto max-w-3xl px-6 py-12" data-test="screen-styles">
      <h1 class="mb-2 text-2xl font-bold text-gray-900">Base Styles</h1>
      <p class="mb-10 text-sm text-gray-500">
        The theme-aware color tokens and type scale available across larimar. These swap
        automatically with the light/dark toggle — the values below are CSS custom properties, not
        hardcoded hex.
      </p>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Neutral palette</h2>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          <div
            v-for="swatch in swatches"
            :key="swatch.name"
            class="overflow-hidden rounded-lg border border-gray-300"
          >
            <div class="h-16" :class="swatch.bgClass" />
            <div class="px-3 py-2 text-xs font-medium text-gray-700">{{ swatch.name }}</div>
          </div>
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Accent</h2>
        <div class="flex flex-wrap items-center gap-4">
          <div class="overflow-hidden rounded-lg border border-gray-300">
            <div class="h-16 w-32 bg-indigo-600" />
            <div class="px-3 py-2 text-xs font-medium text-gray-700">indigo-600 (action)</div>
          </div>
          <span class="text-indigo-600">Non-default</span>
          <a href="#" class="text-indigo-600 hover:text-indigo-400">link example</a>
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Typography</h2>
        <div class="space-y-3">
          <h1 class="text-2xl font-bold text-gray-900">Heading / text-2xl font-bold</h1>
          <h2 class="text-xl font-semibold text-gray-900">Heading / text-xl font-semibold</h2>
          <h3 class="text-lg font-semibold text-gray-900">Heading / text-lg font-semibold</h3>
          <p class="text-sm/6 font-medium text-gray-900">Body / text-sm font-medium</p>
          <p class="text-sm text-gray-500">Secondary / text-sm text-gray-500</p>
          <p class="text-xs font-bold text-gray-900">Label / text-xs font-bold</p>
        </div>
      </section>

      <section>
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Surfaces</h2>
        <div class="grid grid-cols-2 gap-4">
          <div class="ring-white5 rounded-lg bg-white p-4 ring-1">
            <p class="text-sm text-gray-900">bg-white surface (sidebar/content)</p>
          </div>
          <div class="rounded-lg bg-gray-100 p-4">
            <p class="text-sm text-gray-900">bg-gray-100 surface (hover/active state)</p>
          </div>
        </div>
      </section>
    </div>
  </SidebarLayout>
</template>
